import { useCallback, useEffect, useState } from 'react'

import useEmblaCarousel from 'embla-carousel-react'
import type { UseEmblaCarouselType } from 'embla-carousel-react'

const SCROLL_AMOUNT = 320
const SEE_MORE_ARROW_HIDE_THRESHOLD = 140

type UseRecommendationCarouselNavProps = {
  hasSeeMoreCard: boolean
}

type UseRecommendationCarouselNavReturn = {
  emblaRef: UseEmblaCarouselType[0]
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  onPrevButtonClick: () => void
  onNextButtonClick: () => void
}

const getRemainingScrollRight = (emblaApi: NonNullable<UseEmblaCarouselType[1]>) => {
  const containerNode = emblaApi.containerNode()
  const rootNode = emblaApi.rootNode()
  const maxScrollLeft = Math.max(containerNode.scrollWidth - rootNode.clientWidth, 0)

  return Math.max(maxScrollLeft - containerNode.scrollLeft, 0)
}

export const useRecommendationCarouselNav = ({
  hasSeeMoreCard,
}: UseRecommendationCarouselNavProps): UseRecommendationCarouselNavReturn => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, slidesToScroll: 'auto' })
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) {
      return
    }

    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) {
      return
    }

    const remainingScrollRight = getRemainingScrollRight(emblaApi)

    if (hasSeeMoreCard && remainingScrollRight <= SCROLL_AMOUNT) {
      emblaApi.scrollTo(emblaApi.scrollSnapList().length - 1)
      return
    }

    emblaApi.scrollNext()
  }, [emblaApi, hasSeeMoreCard])

  const onSelect = useCallback(
    (inputEmblaApi: UseEmblaCarouselType[1]) => {
      if (!inputEmblaApi) {
        setPrevBtnDisabled(true)
        setNextBtnDisabled(true)
        return
      }

      const remainingScrollRight = getRemainingScrollRight(inputEmblaApi)
      const nextButtonHideThreshold = hasSeeMoreCard ? SEE_MORE_ARROW_HIDE_THRESHOLD : 0

      setPrevBtnDisabled(!inputEmblaApi.canScrollPrev())
      setNextBtnDisabled(
        !inputEmblaApi.canScrollNext() || remainingScrollRight <= nextButtonHideThreshold,
      )
    },
    [hasSeeMoreCard],
  )

  useEffect(() => {
    if (emblaApi) {
      onSelect(emblaApi)

      emblaApi.on('reInit', onSelect).on('select', onSelect).on('settle', onSelect)
    }

    return () => {
      emblaApi?.off('reInit', onSelect)
      emblaApi?.off('select', onSelect)
      emblaApi?.off('settle', onSelect)
    }
  }, [emblaApi, onSelect])

  return {
    emblaRef,
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  }
}
